import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AcceptInvite from "./pages/AcceptInvite";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import OwnerDashboard from "./pages/owner/Dashboard";
import OwnerWorkers from "./pages/owner/Workers";
import OwnerClients from "./pages/owner/Clients";
import OwnerCalendar from "./pages/owner/Calendar";
import OwnerMaterials from "./pages/owner/Materials";
import OwnerReports from "./pages/owner/Reports";
import OwnerSettings from "./pages/owner/Settings";
import OwnerTemplates from "./pages/owner/Templates";
import OwnerSubscription from "./pages/owner/Subscription";
import SubscriptionSuccess from "./pages/owner/SubscriptionSuccess";
import OwnerMonthlySummary from "./pages/owner/MonthlySummary";
import WorkerDashboard from "./pages/worker/Dashboard";
import WorkerJob from "./pages/worker/Job";
import WorkerHistory from "./pages/worker/History";
import WorkerCalendar from "./pages/worker/Calendar";
import WorkerAdHocReport from "./pages/worker/AdHocReport";
import OwnerChat from "./pages/owner/Chat";
import OwnerNewReport from "./pages/owner/NewReport";
import OwnerAnalytics from "./pages/owner/Analytics";
import RouteOptimization from "./pages/owner/RouteOptimization";
import MyJobs from "./pages/owner/MyJobs";
import CompleteJob from "./pages/owner/CompleteJob";
import WorkerChat from "./pages/worker/Chat";
import OwnerGuide from "./pages/owner/Guide";
import Quotes from "./pages/owner/Quotes";
import QuoteEdit from "./pages/owner/QuoteEdit";
import AvisoLegal from "./pages/legal/AvisoLegal";
import PoliticaPrivacidad from "./pages/legal/PoliticaPrivacidad";
import TerminosCondiciones from "./pages/legal/TerminosCondiciones";
import PoliticaCookies from "./pages/legal/PoliticaCookies";
import CookieBanner from "./components/CookieBanner";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles, requireSubscription = true }) => {
  const { user, loading, isAuthenticated, requiresSubscription, subscriptionMessage } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === 'owner' ? '/dashboard' : '/worker'} replace />;
  }
  
  // Check subscription requirement (skip for subscription page itself)
  if (requireSubscription && requiresSubscription && user?.role === 'owner') {
    return <Navigate to="/subscription" replace />;
  }
  
  return children;
};

// Public Route - redirect if logged in
const PublicRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to={user.role === 'owner' ? '/dashboard' : '/worker'} replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      
      {/* Legal Pages */}
      <Route path="/aviso-legal" element={<AvisoLegal />} />
      <Route path="/politica-privacidad" element={<PoliticaPrivacidad />} />
      <Route path="/terminos-condiciones" element={<TerminosCondiciones />} />
      <Route path="/politica-cookies" element={<PoliticaCookies />} />
      
      {/* Owner Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <OwnerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/workers" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <OwnerWorkers />
        </ProtectedRoute>
      } />
      <Route path="/clients" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <OwnerClients />
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <OwnerCalendar />
        </ProtectedRoute>
      } />
      <Route path="/materials" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <OwnerMaterials />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <OwnerReports />
        </ProtectedRoute>
      } />
      <Route path="/templates" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <OwnerTemplates />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <OwnerSettings />
        </ProtectedRoute>
      } />
      <Route path="/summary" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <OwnerMonthlySummary />
        </ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute allowedRoles={['owner']} requireSubscription={false}>
          <OwnerSubscription />
        </ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute allowedRoles={['owner']} requireSubscription={false}>
          <SubscriptionSuccess />
        </ProtectedRoute>
      } />
      <Route path="/new-report" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <OwnerNewReport />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <OwnerAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/routes" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <RouteOptimization />
        </ProtectedRoute>
      } />
      <Route path="/my-jobs" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <MyJobs />
        </ProtectedRoute>
      } />
      <Route path="/complete-job/:jobId" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <CompleteJob />
        </ProtectedRoute>
      } />
      <Route path="/guide" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <OwnerGuide />
        </ProtectedRoute>
      } />
      <Route path="/quotes" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <Quotes />
        </ProtectedRoute>
      } />
      <Route path="/quotes/new" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <QuoteEdit />
        </ProtectedRoute>
      } />
      <Route path="/quotes/:quoteId" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <QuoteEdit />
        </ProtectedRoute>
      } />
      <Route path="/quotes/:quoteId/edit" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <QuoteEdit />
        </ProtectedRoute>
      } />
      
      {/* Worker Routes */}
      <Route path="/worker" element={
        <ProtectedRoute allowedRoles={['worker']}>
          <WorkerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/worker/job/:jobId" element={
        <ProtectedRoute allowedRoles={['worker']}>
          <WorkerJob />
        </ProtectedRoute>
      } />
      <Route path="/worker/history" element={
        <ProtectedRoute allowedRoles={['worker']}>
          <WorkerHistory />
        </ProtectedRoute>
      } />
      <Route path="/worker/calendar" element={
        <ProtectedRoute allowedRoles={['worker']}>
          <WorkerCalendar />
        </ProtectedRoute>
      } />
      <Route path="/worker/new-report" element={
        <ProtectedRoute allowedRoles={['worker']}>
          <WorkerAdHocReport />
        </ProtectedRoute>
      } />
      <Route path="/worker/chat" element={
        <ProtectedRoute allowedRoles={['worker']}>
          <WorkerChat />
        </ProtectedRoute>
      } />
      
      {/* Owner Chat */}
      <Route path="/chat" element={
        <ProtectedRoute allowedRoles={['owner']}>
          <OwnerChat />
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
          <CookieBanner />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
